import { lazy, type ComponentType } from 'react'

/**
 * Wrapper around `React.lazy` that self-heals stale lazy-chunk failures.
 *
 * Vite builds with content-hashed chunk filenames, so every deploy produces new
 * names. A tab left open across a deploy still runs the old `index.html`, which
 * references chunk hashes that no longer exist on S3. When such a tab navigates
 * to a lazy route the dynamic `import()` rejects (the SPA rewrite serves
 * `index.html` / `text/html` for the missing `.js`), `React.lazy` rethrows during
 * render, and — with no error boundary — the whole app unmounts to a blank page.
 *
 * This wrapper catches that rejection and reloads the page ONCE to pick up the
 * fresh `index.html` + chunk hashes. A `sessionStorage` guard prevents an
 * infinite reload loop when the failure is genuine (real outage) rather than
 * staleness — the second failure is re-thrown for the ErrorBoundary to catch.
 */

const RELOAD_GUARD_KEY = 'gisify_chunk_reload'

// Browsers word dynamic-import / module-script failures differently, and they do
// NOT share phrasing — a pattern list built from one engine silently fails on the
// others. Each entry below is a real message, lower-cased before the test:
//
//   Chrome   "Failed to fetch dynamically imported module: https://…"
//   Chrome   "Failed to load module script: Expected a JavaScript-or-Wasm module
//             script but the server responded with a MIME type of "text/html"."
//   Firefox  "error loading dynamically imported module: https://…"
//   Firefox  "Loading module from “…” was blocked because of a disallowed MIME
//             type (“text/html”)."
//   WebKit   "'text/html' is not a valid JavaScript MIME type for module https://…"
//   WebKit   "Importing a module script failed."
//
// The WebKit MIME wording shares NO substring with Chrome's — it says "is not a
// valid JavaScript MIME type" where Chrome says "Expected a JavaScript". Missing
// it meant Safari users got the ErrorBoundary's "Something went wrong" after a
// deploy instead of the silent reload, and "Try Again" re-ran the same stale
// import and failed identically. Reported from production.
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'failed to load module script',
  'importing a module script failed',
  'expected a javascript', // Chrome MIME mismatch
  'is not a valid javascript mime type', // WebKit MIME mismatch
  'disallowed mime type', // Firefox MIME mismatch
]

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  const lower = message.toLowerCase()

  return CHUNK_ERROR_PATTERNS.some(pattern => lower.includes(pattern))
}

function readGuard(): string | null {
  try {
    return window.sessionStorage.getItem(RELOAD_GUARD_KEY)
  } catch {
    // sessionStorage can throw in locked-down/private contexts — treat as unset.
    return null
  }
}

function setGuard(): void {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    // Best-effort only; without the guard we still reload, just can't loop-protect.
  }
}

function clearGuard(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_GUARD_KEY)
  } catch {
    // No-op — nothing to clear if storage is unavailable.
  }
}

/**
 * Wraps a dynamic-import function with stale-chunk retry behaviour. Exported
 * (separate from `lazyWithRetry`) so it can be unit-tested without rendering a
 * React tree.
 */
export function createRetryingLoader<T extends ComponentType<unknown>>(importFn: () => Promise<{ default: T }>) {
  return async (): Promise<{ default: T }> => {
    try {
      const module = await importFn()
      // Successful load — clear the guard so a future stale deploy self-heals too.
      clearGuard()

      return module
    } catch (error) {
      if (!isChunkLoadError(error)) {
        throw error
      }

      if (readGuard()) {
        // Already reloaded once and the chunk still fails — not staleness.
        // Re-throw so the ErrorBoundary catches it instead of looping.
        throw error
      }

      setGuard()
      window.location.reload()

      // The page is navigating away; block forever so React keeps showing the
      // Suspense fallback rather than surfacing a crash before the reload lands.
      return new Promise<{ default: T }>(() => {})
    }
  }
}

export function lazyWithRetry<T extends ComponentType<unknown>>(importFn: () => Promise<{ default: T }>) {
  return lazy(createRetryingLoader(importFn))
}
